/**
 * global.js — Global utilities for the Hydrodiff Shopify theme.
 *
 * Provides:
 *   - CartAPI: Fetch-based wrapper around the Shopify Cart AJAX API
 *   - PubSub: Lightweight publish/subscribe event bus
 *   - debounce(): Utility to limit how often a function fires
 *   - Quantity-selector wiring on DOMContentLoaded
 *
 * No dependencies — plain vanilla JS loaded via <script> tag.
 */

/* -------------------------------------------------------
   1. PubSub — simple publish / subscribe event system
   ------------------------------------------------------- */

const PubSub = (function () {
  /** @type {Object<string, Function[]>} */
  const subscribers = {};

  return {
    /**
     * Subscribe to a named event.
     * @param {string}   event    Event name (e.g. "cart-update").
     * @param {Function} callback Handler called with the event data.
     * @returns {Function} Unsubscribe function.
     */
    subscribe(event, callback) {
      if (!subscribers[event]) {
        subscribers[event] = [];
      }
      subscribers[event].push(callback);

      // Return a function that removes this specific subscription
      return function unsubscribe() {
        subscribers[event] = subscribers[event].filter((cb) => cb !== callback);
      };
    },

    /**
     * Publish a named event to all current subscribers.
     * @param {string} event Event name.
     * @param {*}      data  Payload passed to every subscriber.
     */
    publish(event, data) {
      if (!subscribers[event]) return;
      subscribers[event].forEach((callback) => {
        try {
          callback(data);
        } catch (err) {
          console.error(`[PubSub] Error in subscriber for "${event}":`, err);
        }
      });
    },
  };
})();

/* -------------------------------------------------------
   2. CartAPI — Shopify AJAX Cart wrapper
   ------------------------------------------------------- */

const CartAPI = (function () {
  const HEADERS = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  /**
   * Internal helper that performs a fetch request, parses JSON,
   * and publishes a "cart-update" event on success.
   *
   * @param {string} url        Shopify cart endpoint.
   * @param {string} method     HTTP method (GET or POST).
   * @param {Object} [body]     Request body (will be JSON-stringified).
   * @param {boolean} [notify]  Whether to publish a cart-update event (default true).
   * @returns {Promise<Object>}  Parsed JSON response.
   */
  async function request(url, method, body = null, notify = true) {
    try {
      const options = { method, headers: HEADERS };

      if (body) {
        options.body = JSON.stringify(body);
      }

      const response = await fetch(url, options);

      // Shopify returns 422 for invalid quantities, 404 for bad endpoints, etc.
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const message =
          errorBody.message || errorBody.description || response.statusText;
        throw new Error(`Cart request failed (${response.status}): ${message}`);
      }

      const data = await response.json();

      // Let the rest of the app know the cart changed
      if (notify) {
        PubSub.publish('cart-update', data);

        // Announce cart update to screen readers
        var liveRegion = document.getElementById('cart-live-region');
        if (liveRegion) {
          liveRegion.textContent = data.item_count + ' article' + (data.item_count > 1 ? 's' : '') + ' dans le panier';
        }
      }

      return data;
    } catch (err) {
      console.error('[CartAPI]', err);
      throw err; // Re-throw so callers can handle it too
    }
  }

  return {
    /**
     * Fetch the current cart state.
     * @returns {Promise<Object>} Full cart object.
     */
    get() {
      return request('/cart.js', 'GET', null, false);
    },

    /**
     * Add one or more items to the cart.
     * @param {Object|Object[]} items - A single item or array of items.
     *   Each item should have at least { id, quantity }.
     * @returns {Promise<Object>} Response with added line items.
     */
    add(items) {
      const payload = { items: Array.isArray(items) ? items : [items] };
      return request('/cart/add.js', 'POST', payload);
    },

    /**
     * Bulk-update cart line quantities by variant ID.
     * @param {Object} updates - Map of variant ID -> new quantity.
     *   Example: { 12345: 2, 67890: 0 }
     * @returns {Promise<Object>} Updated cart object.
     */
    update(updates) {
      return request('/cart/update.js', 'POST', { updates });
    },

    /**
     * Change a single line item's quantity by its 1-based line index.
     * @param {number} line     1-based line number.
     * @param {number} quantity New quantity (0 to remove).
     * @returns {Promise<Object>} Updated cart object.
     */
    change(line, quantity) {
      return request('/cart/change.js', 'POST', { line, quantity });
    },

    /**
     * Remove all items from the cart.
     * @returns {Promise<Object>} Empty cart object.
     */
    clear() {
      return request('/cart/clear.js', 'POST', {});
    },
  };
})();

/* -------------------------------------------------------
   3. debounce — Utility function
   ------------------------------------------------------- */

/**
 * Returns a debounced version of `fn` that delays invocation
 * until `delay` ms have passed since the last call.
 *
 * @param {Function} fn    Function to debounce.
 * @param {number}   delay Milliseconds to wait (default 300).
 * @returns {Function} Debounced function.
 */
function debounce(fn, delay = 300) {
  let timer = null;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/* -------------------------------------------------------
   4. formatMoney — Price formatting matching Shopify | money
   ------------------------------------------------------- */

/**
 * Format a price in cents using the shop's money_format.
 * Produces output identical to the Liquid {{ amount | money }} filter.
 * @param {number} cents  Price in cents (subunit).
 * @returns {string}  Formatted price matching the store's money_format.
 */
function formatMoney(cents) {
  var amount = (cents / 100).toFixed(2);
  var parts = amount.split('.');
  var whole = parts[0];
  var decimal = parts[1];

  function addSep(w) {
    return w.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  /* Format français : toujours deux décimales — 2 341,00€ ou 2 341,50€ */
  return addSep(whole) + ',' + decimal + '€';
}

/* -------------------------------------------------------
   4b. Tax mode — HT/TTC toggle (persisted in localStorage)
   ------------------------------------------------------- */

window.Hydrodiff = window.Hydrodiff || {};

/* Tax mode forced to TTC — toggle removed (decision 2026-03-17) */
window.Hydrodiff.taxMode = 'ttc';

/**
 * Convert a TTC price to HT or leave as-is based on current tax mode.
 * @param {number} centsTTC  Price in centimes TTC.
 * @returns {number}  Centimes TTC or HT depending on mode.
 */
function getDisplayPrice(centsTTC) {
  if (window.Hydrodiff.taxMode === 'ht') {
    return Math.round(centsTTC * 100 / 120);
  }
  return centsTTC;
}

/**
 * Get the current tax label.
 * @returns {string} "TTC" or "HT"
 */
function getTaxLabel() {
  return window.Hydrodiff.taxMode === 'ht' ? 'HT' : 'TTC';
}

/* -------------------------------------------------------
   5. Quantity selector wiring
   ------------------------------------------------------- */

/**
 * Wire up all `.quantity-selector` components on the page.
 *
 * Expected markup (inside each .quantity-selector):
 *   <button data-quantity-minus>-</button>
 *   <input  data-quantity-input type="number" value="1" min="1">
 *   <button data-quantity-plus>+</button>
 */
function initQuantitySelectors() {
  const selectors = document.querySelectorAll('.quantity-selector');

  selectors.forEach((selector) => {
    const input = selector.querySelector('[data-quantity-input]');
    const minusBtn = selector.querySelector('[data-quantity-minus]');
    const plusBtn = selector.querySelector('[data-quantity-plus]');

    if (!input) return; // Nothing to do without an input

    const min = parseInt(input.getAttribute('min'), 10) || 1;
    const max = parseInt(input.getAttribute('max'), 10) || Infinity;

    /**
     * Clamp the input value within allowed bounds and dispatch
     * a "change" event so other listeners (e.g. cart.js) can react.
     */
    function setQuantity(newValue) {
      const clamped = Math.min(Math.max(newValue, min), max);
      input.value = clamped;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (minusBtn) {
      minusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        setQuantity(parseInt(input.value, 10) - 1);
      });
    }

    if (plusBtn) {
      plusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        setQuantity(parseInt(input.value, 10) + 1);
      });
    }

    // Sanitise manual edits when the user leaves the field
    input.addEventListener('blur', () => {
      const parsed = parseInt(input.value, 10);
      if (isNaN(parsed) || parsed < min) {
        setQuantity(min);
      } else {
        setQuantity(parsed);
      }
    });
  });
}

/* -------------------------------------------------------
   6. Bootstrap on DOMContentLoaded
   ------------------------------------------------------- */

document.addEventListener('DOMContentLoaded', () => {
  initQuantitySelectors();

  /* --- Global price normalizer: format all [data-price-cents] elements --- */
  function updateAllPrices() {
    document.querySelectorAll('[data-price-cents]').forEach(function (el) {
      var raw = el.getAttribute('data-price-cents');
      var cents = parseInt(raw, 10) || 0;
      var prefix = el.hasAttribute('data-price-prefix') ? el.getAttribute('data-price-prefix') : '';
      var text = prefix + formatMoney(getDisplayPrice(cents)) + ' ' + getTaxLabel();
      /* Preserve <s> wrapper for compare-at prices */
      if (el.tagName === 'S') {
        el.textContent = text;
      } else if (el.querySelector('s')) {
        el.querySelector('s').textContent = text;
      } else {
        el.textContent = text;
      }
    });
  }

  /* Expose globally for dynamic content (load-more, etc.) */
  window.Hydrodiff.updateAllPrices = updateAllPrices;

  /* On tax mode change */
  PubSub.subscribe('tax-mode-change', updateAllPrices);

  /* On page load: always normalize server-rendered prices to French format + TTC/HT label */
  updateAllPrices();

  /* --- Tax toggle wiring --- */
  var taxToggle = document.querySelector('[data-tax-toggle]');
  if (taxToggle) {
    var ttcOption = taxToggle.querySelector('[data-tax-ttc]');
    var htOption = taxToggle.querySelector('[data-tax-ht]');

    function applyTaxMode(mode) {
      window.Hydrodiff.taxMode = mode;
      try { localStorage.setItem('hydrodiff_tax_mode', mode); } catch (e) { /* noop */ }

      if (ttcOption && htOption) {
        ttcOption.classList.toggle('header__tax-toggle-option--active', mode === 'ttc');
        htOption.classList.toggle('header__tax-toggle-option--active', mode === 'ht');
        taxToggle.classList.toggle('header__tax-toggle--ht', mode === 'ht');
      }

      PubSub.publish('tax-mode-change', { mode: mode });
    }

    /* Initialise visual state from stored mode */
    if (window.Hydrodiff.taxMode === 'ht') {
      applyTaxMode('ht');
    }

    taxToggle.addEventListener('click', function () {
      applyTaxMode(window.Hydrodiff.taxMode === 'ttc' ? 'ht' : 'ttc');
    });
  }
});
